/**
 * IntroVideo — fullscreen cinematic video screen shown after login.
 *
 * Cannot be skipped — video must play to completion. The overlay does
 * auto-dismiss if playback fails (handled by VideoInterstitial's
 * watchdogs), so the user is never trapped on a blank screen.
 *
 * On mount we record today's SAST date in user_profiles.intro_video_last_played_date
 * so the gate in AuthProvider.selectRole skips the video on every subsequent
 * open the same day, even if the user closes the tab mid-playback.
 *
 * On video end (or auto-dismiss) we clear the intro flag and navigate to
 * the dashboard. Uses the reusable VideoInterstitial component for playback.
 */

import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import VideoInterstitial from '../components/shared/VideoInterstitial';
import { markIntroVideoPlayedToday } from '../api/userProfilesApi';
import { getTodaySASTDateString } from '../utils/dateHelpers';

// Served from public/intro-video.mp4 — bundled with the Cloudflare Pages
// deploy so playback does not depend on Supabase Storage availability or
// signed-URL round-trips.
const VIDEO_URL = '/intro-video.mp4';

export default function IntroVideo() {
  const navigate = useNavigate();
  const { clearIntroVideo, user } = useAuth();

  // Record the play on mount (fire and forget). A successful write means
  // the gate in AuthProvider.selectRole will skip the video on every
  // subsequent open today — across devices, browsers, refreshes, re-logins.
  // Errors are swallowed by the API helper; the worst case is the video
  // plays once more.
  useEffect(() => {
    if (user?.id) {
      markIntroVideoPlayedToday(user.id, getTodaySASTDateString());
    }
  }, [user?.id]);

  const proceed = useCallback(() => {
    clearIntroVideo();
    navigate('/', { replace: true });
  }, [clearIntroVideo, navigate]);

  return <VideoInterstitial videoUrl={VIDEO_URL} onComplete={proceed} />;
}
